import { ChevronRight, FileText, Files, MessageSquare } from "lucide-react";
import { Text } from "@core/index";
import Modal from "@core/modal";
import { EssayGradingResultDto } from "@packages/api/madlenAPI.schemas";
import { useToast } from "@packages/contexts/ToastContext";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useState } from "react";

interface HistoryModalProps {
  show: boolean;
  onClose: () => void;
  chats: EssayGradingResultDto[];
  onSelectChat: (chatId: number, subId?: number) => void;
  onShowTable?: (chatId: number) => void;
  isLoading: boolean;
}

export function HistoryModal({
  show,
  onClose,
  chats,
  onSelectChat,
  onShowTable,
  isLoading,
}: HistoryModalProps) {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();

  return (
    <Modal show={show} onClose={onClose} title={t("assistant.chatHistory")}>
      <div className="space-y-4 max-h-[500px] overflow-y-auto">
        {chats?.map((chat) => {
          const hasMultipleResults = (chat.results?.length || 0) > 1;
          const resultsCount = chat.results?.length || 0;

          return (
            <div
              key={chat.id}
              className={`border rounded-lg overflow-hidden ${
                hasMultipleResults
                  ? "border-primary/30 dark:border-primary/40"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              {/* Main chat row */}
              <div
                onClick={() => {
                  if (hasMultipleResults) {
                    // Go directly to table for multiple essays
                    if (chat?.id && onShowTable) {
                      onShowTable(chat.id);
                    } else {
                      addToast({
                        title: t("assistant.essayGrader.error"),
                        description: t(
                          "assistant.essayGrader.errorDescription",
                        ),
                        variant: "error",
                      });
                    }
                  } else if (chat?.id) {
                    // If only one result, navigate directly to it
                    const subId = chat.results?.[0]?.id;
                    onSelectChat(chat.id, subId);
                  } else {
                    addToast({
                      title: t("assistant.essayGrader.error"),
                      description: t("assistant.essayGrader.errorDescription"),
                      variant: "error",
                    });
                  }
                }}
                className={`flex items-center p-4 transition-colors group cursor-pointer ${
                  hasMultipleResults
                    ? "hover:bg-primary/5 dark:hover:bg-primary/10"
                    : "hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                {hasMultipleResults ? (
                  <Files className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                ) : (
                  <FileText className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between space-x-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {hasMultipleResults
                        ? chat.rubricFile?.fileName
                        : chat.results?.[0]?.essayFile?.fileName ||
                          chat.essayFiles?.[0]?.fileName ||
                          t("assistant.noDescription")}
                    </h4>
                  </div>
                  <Text
                    variant="p"
                    status="secondary"
                    size="xs"
                    className="mt-1 truncate"
                  >
                    {format(
                      new Date(chat.createdAt || ""),
                      "d MMMM yyyy HH:mm",
                      {
                        locale: i18n.language === "tr" ? tr : enUS,
                      },
                    )}
                  </Text>
                </div>
                {hasMultipleResults && (
                  <span className="inline-flex items-center px-2 rounded text-xs font-medium bg-primary/10 text-primary">
                    {resultsCount} {t("assistant.essayGrader.essays")}
                  </span>
                )}
                {!hasMultipleResults && (
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 ml-4" />
                )}
              </div>
            </div>
          );
        })}

        {isLoading && (
          <div className="p-4 text-center">
            <Text variant="p" status="secondary">
              {t("common.loading")}
            </Text>
          </div>
        )}

        {(!chats || chats.length === 0) && !isLoading && (
          <div className="p-4 text-center">
            <Text variant="p" status="secondary">
              {t("assistant.noHistory")}
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
}
